import { isOneOf } from '@shared/helpers/array.helpers';
import {
  isDeactivated,
  isDeleted,
  shouldHideProductElement,
} from '@shared/helpers/element.helpers';
import { getProductElementData } from '@shared/helpers/product-element.helpers';
import {
  getChildElements,
  isElement,
  isProductElement,
} from '@shared/helpers/recursive-element.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { isA5SbefCode } from '@shared/templates/categories/energy/energy.helpers';
import type {
  IProductElement,
  OneOfElements,
  ProjectMarketEstimates,
} from '@shared/models/project.model';
import type { ProductRecord } from '@shared/models/product.model';
import type { Results } from '@shared/models/unit.model';
import { getPropertyValuesFromProducts } from '@features/products/ProductBulkEditForm/product-bulk-edit-form.helpers';
import { ElementCategoryID } from '@shared/models/element-categories.model';

/**
 * Categories whose `mapResults` moves kWh products from the A-stage to B6.
 * Same unit check as heating, cooling, and air-handling-unit.
 */
const KWH_MOVED_TO_B6_CATEGORIES = [
  ElementCategoryID.Heating,
  ElementCategoryID.Cooling,
  ElementCategoryID.AirHandlingUnit,
] as const;

/** Product unit those category mappers treat as operational energy. */
const LIFECYCLE_MAPPED_KWH_UNIT = 'kWh';

/** Phases a category mapper writes when it takes the A-stage off A1–A3. */
const MOVED_CO2_PHASES = ['co2e_A5', 'co2e_B6'] as const;

type MovedCo2Phase = (typeof MOVED_CO2_PHASES)[number];

/** Moved amount per phase. A key is present once a mapped product is counted, even at 0. */
type MovedCo2 = Partial<Record<MovedCo2Phase, number>>;

/**
 * Live product carbon for one element row.
 * `a1a3` replaces stored A1–A3. `clearA1A3` removes it when the category moved
 * the whole A-stage. `co2e_A5` / `co2e_B6` replace that phase with the amount
 * the mapper would have written, so the list updates before the next enrich.
 */
export type LiveProductCo2 = {
  a1a3: number;
  clearA1A3?: boolean;
  co2e_A5?: number;
  co2e_B6?: number;
};

/** A category `mapResults` that can take the A-stage off A1–A3. */
type LifecycleMapper =
  | { kind: 'energy'; phase: MovedCo2Phase }
  | { kind: 'kwhToB6' }
  | { kind: 'maintenance' };

/**
 * Mappers on the path to an element, closest first, the order
 * `mapResultsByElementCategories` applies them in.
 */
type LiveCo2Context = readonly LifecycleMapper[];

/** Where a product's A-stage ends up. `omitted` is Maintenance B2, which this walk cannot compute. */
type ProductPhase = 'co2e_A1-A3' | MovedCo2Phase | 'omitted';

type VisitTotal = {
  a1a3: number;
  moved: MovedCo2;
};

const ownMapper = (element: OneOfElements): LifecycleMapper | undefined => {
  if (!isElement(element)) return undefined;
  if (element.category_id === ElementCategoryID.Energy) {
    return {
      kind: 'energy',
      phase: isA5SbefCode(element) ? 'co2e_A5' : 'co2e_B6',
    };
  }
  if (element.category_id === ElementCategoryID.Maintenance) {
    return { kind: 'maintenance' };
  }
  return isOneOf(KWH_MOVED_TO_B6_CATEGORIES, element.category_id)
    ? { kind: 'kwhToB6' }
    : undefined;
};

const contextFor = (
  element: OneOfElements,
  parent: LiveCo2Context,
): LiveCo2Context => {
  const own = ownMapper(element);
  return own ? [own, ...parent] : parent;
};

const hasMapper = (
  context: LiveCo2Context,
  kind: LifecycleMapper['kind'],
): boolean => context.some((mapper) => mapper.kind === kind);

/** Phase after one mapper, for a product whose A-stage is still in A1–A3 or A5. */
const applyMapper = (
  mapper: LifecycleMapper,
  phase: ProductPhase,
  product: IProductElement,
  productsMap: ProductRecord | undefined,
): ProductPhase => {
  switch (mapper.kind) {
    case 'energy':
      return mapper.phase;
    case 'kwhToB6':
      return product.unit === LIFECYCLE_MAPPED_KWH_UNIT ? 'co2e_B6' : phase;
    case 'maintenance':
      return keepsA1A3DespiteMaintenance(product, productsMap)
        ? phase
        : 'omitted';
    default:
      return assertUnreachable(mapper);
  }
};

/**
 * Runs the mappers closest first, like the pipeline. A5 is still part of
 * `co2e_A`, so an outer mapper moves it again. B6 and B2 are not, so they are final.
 */
const productPhaseFor = (
  product: IProductElement,
  context: LiveCo2Context,
  productsMap: ProductRecord | undefined,
): ProductPhase => {
  let phase: ProductPhase = 'co2e_A1-A3';
  for (const mapper of context) {
    phase = applyMapper(mapper, phase, product, productsMap);
    if (phase === 'co2e_B6' || phase === 'omitted') return phase;
  }
  return phase;
};

/**
 * Phases whose stored value on this row is only moved A-stage, so a live total
 * can replace it. Under Energy every product is moved. Under Heating, Cooling,
 * or AHU alone, other products keep their own A5, so only B6 is replaced.
 */
const replaceablePhases = (
  context: LiveCo2Context,
): readonly MovedCo2Phase[] => {
  if (hasMapper(context, 'energy')) return MOVED_CO2_PHASES;
  return hasMapper(context, 'kwhToB6') ? ['co2e_B6'] : [];
};

const addMovedCo2 = (target: MovedCo2, source: MovedCo2): void => {
  for (const phase of MOVED_CO2_PHASES) {
    const value = source[phase];
    if (value === undefined) continue;
    target[phase] = (target[phase] ?? 0) + value;
  }
};

/**
 * Row patch for one element. A1–A3 is cleared under Energy, or when it is 0 and
 * a replaceable phase holds the moved amount.
 */
const toLiveProductCo2 = (
  a1a3: number,
  moved: MovedCo2,
  context: LiveCo2Context,
): LiveProductCo2 => {
  const phases = replaceablePhases(context).filter(
    (phase) => moved[phase] !== undefined,
  );
  const clearA1A3 =
    hasMapper(context, 'energy') || (a1a3 === 0 && phases.length > 0);
  const live: LiveProductCo2 = { a1a3: clearA1A3 ? 0 : a1a3 };
  if (clearA1A3) live.clearA1A3 = true;
  for (const phase of phases) live[phase] = moved[phase];
  return live;
};

/**
 * True when this product is copied onto the baseline product record.
 * The generic catalog is not a substitute: a missing copy is not a factor of 0.
 */
const isProductOnBaseline = (
  product: IProductElement,
  productsMap: ProductRecord | undefined,
): boolean => {
  const data = getProductElementData(product);
  if (guards.is.defined(productsMap?.[data.genericId])) return true;
  switch (data.type) {
    case 'generic':
      return false;
    case 'epd':
      return guards.is.defined(productsMap?.[data.epdId]);
    default:
      return assertUnreachable(data);
  }
};

/**
 * Maintenance `mapResults` keeps the A-stage when the product results already
 * have a truthy B2. The same check, read from the baseline product copy.
 */
const keepsA1A3DespiteMaintenance = (
  product: IProductElement,
  productsMap: ProductRecord | undefined,
): boolean => {
  const data = getProductElementData(product);
  const record =
    productsMap?.[data.genericId] ??
    (data.type === 'epd' ? productsMap?.[data.epdId] : undefined);
  const factors = record?.conversion_factors;
  return Boolean(factors?.co2e_B2 || factors?.sek_B2);
};

/**
 * Side-panel product total for one carbon key.
 * `co2e_A` is the whole A-stage a category mapper moves, A4 and A5 included.
 *
 * @param products - Baseline products to sum
 * @param property - `co2e_A1-A3` for products that stay, `co2e_A` for moved products
 */
const sumLiveCo2 = (
  products: IProductElement[],
  property: 'co2e_A1-A3' | 'co2e_A',
  productsMap: ProductRecord | undefined,
  marketEstimates: ProjectMarketEstimates | undefined,
): number => {
  if (!products.length) return 0;
  return getPropertyValuesFromProducts(
    productsMap,
    products,
    property,
    'total',
    undefined,
    marketEstimates,
  ).adjustedAverage;
};

/**
 * Live product carbon per element, using the same product totals as the side panel.
 *
 * Elements with no generated products are omitted. Callers keep the stored
 * {@link Results} row for those.
 * When none of an element's own products are on {@link productsMap}, the element
 * is omitted and the stored row stays. A missing baseline copy is not written as 0.
 * A factor that is actually 0 is written as 0.
 *
 * Category `mapResults` is applied to the live value so it is not written back
 * into A1–A3 on top of the phase that already holds it. A moved product
 * contributes its whole A-stage (A1–A3, A4, and A5), like `moveLifecycleAResults`:
 * - Energy: the amount is written to A5 (SBEF 102–105) or B6 (SBEF 101 or none)
 * - Maintenance: products that already have B2 stay in A1–A3. The rest are omitted
 *   because B2 depends on building lifetime, which this walk does not have.
 * - Heating, Cooling, and AHU: kWh products are written to B6. Other units stay in A1–A3.
 *
 * When categories are nested they apply closest first, as in
 * `mapResultsByElementCategories`. A5 stays part of the A-stage, so an outer
 * category moves it again: a kWh product under Energy 102 inside Heating ends in B6,
 * and under Energy 102 inside Maintenance it is omitted.
 *
 * @param root - Resolved element tree the list and the side panel both show
 * @param productsMap - Baseline product copies. Factors are read from this record only.
 * @param marketEstimates - Same snapshot the side panel uses for climate percentiles
 */
export const buildLiveProductA1A3ByElementId = (
  root: OneOfElements | undefined,
  productsMap: ProductRecord | undefined,
  marketEstimates?: ProjectMarketEstimates,
): ReadonlyMap<string, LiveProductCo2> => {
  const totals = new Map<string, LiveProductCo2>();
  if (!root) return totals;

  const isOnBaseline = (product: IProductElement) =>
    isProductOnBaseline(product, productsMap);

  const visit = (
    element: OneOfElements,
    parent: LiveCo2Context,
  ): VisitTotal | undefined => {
    const context = contextFor(element, parent);
    const productsByPhase: Record<
      Exclude<ProductPhase, 'omitted'>,
      IProductElement[]
    > = { 'co2e_A1-A3': [], co2e_A5: [], co2e_B6: [] };
    let a1a3 = 0;
    const moved: MovedCo2 = {};
    let sawNested = false;

    for (const child of getChildElements(element)) {
      if (isElement(child)) {
        if (isDeactivated(child, false) || isDeleted(child)) continue;
        const childTotal = visit(child, context);
        if (!childTotal) continue;
        sawNested = true;
        a1a3 += childTotal.a1a3;
        addMovedCo2(moved, childTotal.moved);
        continue;
      }
      if (!isProductElement(child) || shouldHideProductElement(child)) continue;
      const phase = productPhaseFor(child, context, productsMap);
      if (phase !== 'omitted') productsByPhase[phase].push(child);
    }

    const counted = Object.values(productsByPhase).flat();
    if (counted.length === 0 && !sawNested) return undefined;
    if (counted.length > 0 && !counted.some(isOnBaseline)) return undefined;

    a1a3 += sumLiveCo2(
      productsByPhase['co2e_A1-A3'].filter(isOnBaseline),
      'co2e_A1-A3',
      productsMap,
      marketEstimates,
    );
    for (const phase of MOVED_CO2_PHASES) {
      const onBaseline = productsByPhase[phase].filter(isOnBaseline);
      if (!onBaseline.length) continue;
      addMovedCo2(moved, {
        [phase]: sumLiveCo2(onBaseline, 'co2e_A', productsMap, marketEstimates),
      });
    }

    totals.set(element.id, toLiveProductCo2(a1a3, moved, context));
    return { a1a3, moved };
  };

  visit(root, []);
  return totals;
};

const applyLiveProductCo2 = (
  results: Results,
  patch: LiveProductCo2 | undefined,
): Results => {
  if (!patch) return results;
  const a1Matches = patch.clearA1A3
    ? results['co2e_A1-A3'] === undefined
    : (results['co2e_A1-A3'] ?? 0) === patch.a1a3;
  const a5Matches =
    patch.co2e_A5 === undefined || results.co2e_A5 === patch.co2e_A5;
  const b6Matches =
    patch.co2e_B6 === undefined || results.co2e_B6 === patch.co2e_B6;
  if (a1Matches && a5Matches && b6Matches) return results;

  const next: Results = { ...results };
  if (patch.clearA1A3) {
    delete next['co2e_A1-A3'];
  } else {
    next['co2e_A1-A3'] = patch.a1a3;
  }
  if (patch.co2e_A5 !== undefined) next.co2e_A5 = patch.co2e_A5;
  if (patch.co2e_B6 !== undefined) next.co2e_B6 = patch.co2e_B6;
  return next;
};

/**
 * Applies live carbon to an unfiltered stored row, then applies the display scope.
 * Filtering last keeps excluded phases out and folds stored conservative deltas
 * onto the live base values. SBEF subsets use the stored group buckets until enrich
 * refreshes them; a full live product total cannot replace a scoped subtotal.
 * Without a filter, returns the unfiltered patched row.
 */
export const withLiveProductA1A3 = (
  results: Results,
  live: LiveProductCo2 | undefined,
  filterResults?: (results: Results) => Results,
): Results => {
  const patched = applyLiveProductCo2(results, live);
  return filterResults ? filterResults(patched) : patched;
};

/**
 * Baseline group sum uses the live product total only while no proposal is selected.
 * A selected proposal keeps that bar on the stored row.
 *
 * @param stored - Stored results row for one root element
 * @param liveA1A3 - Live product carbon for that element, or omitted
 * @param selectedProposalId - Proposal currently shown in the list
 */
export const baselineResultsForGroupSum = (
  stored: Results,
  liveA1A3: LiveProductCo2 | undefined,
  selectedProposalId: string | undefined,
  filterResults?: (results: Results) => Results,
): Results => {
  return withLiveProductA1A3(
    stored,
    selectedProposalId ? undefined : liveA1A3,
    filterResults,
  );
};

/**
 * A proposal group sum uses the live product total only for the selected proposal.
 *
 * @param stored - Stored results row for one root element in that proposal
 * @param liveA1A3 - Live product carbon for that element, or omitted
 * @param options.proposalId - Proposal the sum belongs to
 * @param options.selectedProposalId - Proposal currently shown in the list
 */
export const proposalResultsForGroupSum = (
  stored: Results,
  liveA1A3: LiveProductCo2 | undefined,
  options: { proposalId: string; selectedProposalId: string | undefined },
  filterResults?: (results: Results) => Results,
): Results => {
  return withLiveProductA1A3(
    stored,
    options.proposalId === options.selectedProposalId ? liveA1A3 : undefined,
    filterResults,
  );
};
